/**
 * Free Brazilian postal-code lookups. Ordered by reliability + adoption so the
 * best free answer wins before any paid geocode path.
 *
 *  1. BrasilAPI CEP V2  — multi-source, most reliable free CEP+geo
 *  2. AwesomeAPI CEP    — free, often includes lat/lng
 *  3. ViaCEP            — most used free CEP API in Brazil
 *  4. OpenCEP           — popular free production alternative
 *  5. ApiCEP (CDN)
 *  6. BrasilAPI CEP V1
 *
 * Never hits Google. Gateway geocode (free street → Google last) only if the
 * form still needs lat/lng after this cascade.
 */

export type CepLookupResult = {
  street: string
  district: string
  city: string
  state: string
  latitude?: number
  longitude?: number
  source:
    | "brasilapi-cep-v2"
    | "awesomeapi-cep"
    | "viacep"
    | "opencep"
    | "apicep"
    | "brasilapi-cep-v1"
}

type BrasilApiCepResponse = {
  cep?: string
  state?: string
  city?: string
  neighborhood?: string
  street?: string
  location?: {
    type?: string
    coordinates?: { longitude?: string; latitude?: string }
  }
}

type ViaCepLikeResponse = {
  cep?: string
  logradouro?: string
  bairro?: string
  localidade?: string
  uf?: string
  erro?: boolean | string
}

type AwesomeApiCepResponse = {
  cep?: string
  address?: string
  state?: string
  district?: string
  lat?: string
  lng?: string
  city?: string
  status?: number
}

type ApiCepResponse = {
  code?: string
  state?: string
  city?: string
  district?: string
  address?: string
  status?: number
  ok?: boolean
}

function parseCoords(lat?: string, lng?: string): { lat: number; lng: number } | null {
  if (lat == null || lng == null) return null
  const latitude = Number(lat)
  const longitude = Number(lng)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (latitude === 0 && longitude === 0) return null
  return { lat: latitude, lng: longitude }
}

function digitsOnlyCep(cep: string): string {
  return cep.replace(/\D/g, "")
}

function formatCepDash(digits: string): string {
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

async function fetchBrasilApiCepV2(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cleanCep}`)
    if (!response.ok) return null
    const data = (await response.json()) as BrasilApiCepResponse
    if (!data.city && !data.state) return null
    const coords = parseCoords(
      data.location?.coordinates?.latitude,
      data.location?.coordinates?.longitude,
    )
    return {
      street: data.street?.trim() || "",
      district: data.neighborhood?.trim() || "",
      city: data.city?.trim() || "",
      state: data.state?.trim() || "",
      latitude: coords?.lat,
      longitude: coords?.lng,
      source: "brasilapi-cep-v2",
    }
  } catch {
    return null
  }
}

/** AwesomeAPI CEP — free, often includes lat/lng. */
async function fetchAwesomeApiCep(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(`https://cep.awesomeapi.com.br/json/${cleanCep}`)
    if (!response.ok) return null
    const data = (await response.json()) as AwesomeApiCepResponse
    if (data.status === 400 || (!data.city && !data.address)) return null
    const coords = parseCoords(data.lat, data.lng)
    return {
      street: data.address?.trim() || "",
      district: data.district?.trim() || "",
      city: data.city?.trim() || "",
      state: data.state?.trim() || "",
      latitude: coords?.lat,
      longitude: coords?.lng,
      source: "awesomeapi-cep",
    }
  } catch {
    return null
  }
}

async function fetchViaCep(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
    if (!response.ok) return null
    const data = (await response.json()) as ViaCepLikeResponse
    if (data.erro) return null
    if (!data.localidade) return null
    return {
      street: data.logradouro?.trim() || "",
      district: data.bairro?.trim() || "",
      city: data.localidade?.trim() || "",
      state: data.uf?.trim() || "",
      source: "viacep",
    }
  } catch {
    return null
  }
}

async function fetchOpenCep(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(`https://opencep.com/v1/${cleanCep}`)
    if (!response.ok) return null
    const data = (await response.json()) as ViaCepLikeResponse
    if (!data.localidade) return null
    return {
      street: data.logradouro?.trim() || "",
      district: data.bairro?.trim() || "",
      city: data.localidade?.trim() || "",
      state: data.uf?.trim() || "",
      source: "opencep",
    }
  } catch {
    return null
  }
}

/** ApiCEP CDN — free, no coordinates. */
async function fetchApiCep(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(
      `https://cdn.apicep.com/file/apicep/${formatCepDash(cleanCep)}.json`,
    )
    if (!response.ok) return null
    const data = (await response.json()) as ApiCepResponse
    if (data.status && data.status !== 200) return null
    if (!data.city) return null
    let street = data.address?.trim() || ""
    const dash = street.indexOf(" - ")
    if (dash > 0) street = street.slice(0, dash).trim()
    return {
      street,
      district: data.district?.trim() || "",
      city: data.city?.trim() || "",
      state: data.state?.trim() || "",
      source: "apicep",
    }
  } catch {
    return null
  }
}

async function fetchBrasilApiCepV1(cep: string): Promise<CepLookupResult | null> {
  const cleanCep = digitsOnlyCep(cep)
  if (cleanCep.length !== 8) return null
  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${cleanCep}`)
    if (!response.ok) return null
    const data = (await response.json()) as BrasilApiCepResponse
    if (!data.city && !data.state) return null
    return {
      street: data.street?.trim() || "",
      district: data.neighborhood?.trim() || "",
      city: data.city?.trim() || "",
      state: data.state?.trim() || "",
      source: "brasilapi-cep-v1",
    }
  } catch {
    return null
  }
}

/**
 * Free cascade for CEP — never hits Google.
 *
 * BrasilAPI V2 and AwesomeAPI are both tried when possible: fields prefer the
 * first non-empty street/district; coordinates prefer AwesomeAPI when present
 * (often more precise than BrasilAPI city centroids, e.g. DF superquadras).
 */
export async function lookupCep(cep: string): Promise<CepLookupResult | null> {
  const brasilV2 = await fetchBrasilApiCepV2(cep)
  const awesome = await fetchAwesomeApiCep(cep)

  if (brasilV2 || awesome) {
    const street = brasilV2?.street || awesome?.street || ""
    const district = brasilV2?.district || awesome?.district || ""
    const city = brasilV2?.city || awesome?.city || ""
    const state = brasilV2?.state || awesome?.state || ""
    const coordsSource =
      (awesome && hasCepCoordinates(awesome) && awesome) ||
      (brasilV2 && hasCepCoordinates(brasilV2) && brasilV2) ||
      null

    return {
      street,
      district,
      city,
      state,
      latitude: coordsSource ? coordsSource.latitude : undefined,
      longitude: coordsSource ? coordsSource.longitude : undefined,
      source: coordsSource?.source ?? brasilV2?.source ?? awesome!.source,
    }
  }

  return (
    (await fetchViaCep(cep)) ??
    (await fetchOpenCep(cep)) ??
    (await fetchApiCep(cep)) ??
    (await fetchBrasilApiCepV1(cep))
  )
}

export function hasCepCoordinates(result: CepLookupResult | null | undefined): boolean {
  if (!result) return false
  return (
    typeof result.latitude === "number" &&
    typeof result.longitude === "number" &&
    Number.isFinite(result.latitude) &&
    Number.isFinite(result.longitude) &&
    !(result.latitude === 0 && result.longitude === 0)
  )
}
